<template>
  <div class="home-view">
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h1 class="font-heading text-4xl md:text-5xl font-bold mb-4">
            Fundamental Stock Analysis
          </h1>
          <p class="text-teal-100 text-lg md:text-xl max-w-2xl mx-auto">
            Comprehensive analysis of Indonesian stocks listed on the Indonesia Stock Exchange (IDX)
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <div class="bg-white/10 backdrop-blur rounded-lg px-6 py-3">
              <span class="block text-2xl font-bold">{{ stocks.length }}</span>
              <span class="text-sm text-teal-100">Stocks Covered</span>
            </div>
            <div class="bg-white/10 backdrop-blur rounded-lg px-6 py-3">
              <span class="block text-2xl font-bold">11</span>
              <span class="text-sm text-teal-100">Sectors</span>
            </div>
            <div class="bg-white/10 backdrop-blur rounded-lg px-6 py-3">
              <span class="block text-2xl font-bold">13</span>
              <span class="text-sm text-teal-100">Analysis Sections</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stocks List -->
    <section class="py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="mb-8">
        <h2 class="text-2xl font-heading font-bold text-gray-900">All Stocks</h2>
        <p class="text-gray-600 mt-2">Browse and analyze Indonesian stocks by sector</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
        <p class="text-gray-500 mt-4">Loading stocks...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 rounded-xl p-8 text-center">
        <p class="text-red-600">{{ error }}</p>
        <button @click="loadStocks" class="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
          Try Again
        </button>
      </div>

      <!-- Stocks Grid -->
      <div v-else-if="stocks.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="stock in stocks"
          :key="stock.ticker"
          @click="goToStock(stock.ticker)"
          class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-teal-300 transition-all cursor-pointer"
        >
          <div class="flex items-start justify-between mb-4">
            <div>
              <h3 class="text-xl font-bold text-gray-900">{{ stock.ticker }}</h3>
              <p class="text-sm text-gray-500">{{ stock.name }}</p>
            </div>
            <span class="text-xs font-medium px-2 py-1 rounded-full" :class="getSectorClass(stock.sector)">
              {{ stock.sector }}
            </span>
          </div>
          
          <div class="flex items-center justify-between text-sm">
            <span class="text-gray-500">{{ stock.exchange }}</span>
            <span v-if="stock.has_analysis" class="text-teal-600 font-medium">
              Analysis Available
            </span>
            <span v-else class="text-gray-400">
              No Analysis
            </span>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-gray-50 rounded-xl p-12 text-center">
        <p class="text-gray-500 text-lg">No stocks available</p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { fetchStocks } from '@/api/stocks';
import type { StockListItem } from '@stock-dashboard/shared';

const router = useRouter();
const stocks = ref<StockListItem[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

const loadStocks = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    stocks.value = await fetchStocks();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load stocks';
  } finally {
    loading.value = false;
  }
};

const goToStock = (ticker: string) => {
  router.push(`/stocks/${ticker}`);
};

const getSectorClass = (sector: string) => {
  const classes: Record<string, string> = {
    'Financials': 'bg-blue-100 text-blue-700',
    'Technology': 'bg-purple-100 text-purple-700',
    'Consumer': 'bg-green-100 text-green-700',
    'Healthcare': 'bg-red-100 text-red-700',
    'Energy': 'bg-yellow-100 text-yellow-700',
    'Industrials': 'bg-gray-100 text-gray-700',
    'Materials': 'bg-orange-100 text-orange-700',
    'Utilities': 'bg-cyan-100 text-cyan-700',
    'Real Estate': 'bg-pink-100 text-pink-700',
    'Communication': 'bg-indigo-100 text-indigo-700',
  };
  return classes[sector] || 'bg-gray-100 text-gray-700';
};

onMounted(() => {
  loadStocks();
});
</script>
